import './RecReviewSearch.scss';

import { CustomTooltip } from '@/components/custom-tooltip';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Label } from '@/components/ui/label';
import {
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Select as UiSelect,
} from '@/components/ui/select';
import { MenuItem, Select, TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import styles from 'common/_variables.module.scss';
import { weekDays } from 'components/DayPicker/config';
import { Modal } from 'components/Modal/Modal';
import { status as statusOptions } from 'components/StatusPicker/config';
import { LoadingBar } from 'components/loading-bar/loading-bar';
import dayjs from 'dayjs';
import { useGetMaxCustomDateRangeMonthsQuery } from 'features/control-panel/gql/_gen_/control-panel.gql';
import { dateFormatData } from 'features/dates/date-helpers';
import { Field, FieldArray, Form, Formik, type FormikProps } from 'formik';
import type { SearchMetric, UserSearch } from 'graphql/gql-types';
import { labels } from 'locales/en.label';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
	MdClose,
	MdDeleteOutline,
	MdOutlineAdd,
	MdOutlineIndeterminateCheckBox,
	MdRefresh,
} from 'react-icons/md';
import { CUSTOM_DATE_RANGE_VALUE, days } from '../config';

const ALL_DAY_VALUES = weekDays.map((day) => day.value);
const WEEKDAY_VALUES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday'];
const WEEKEND_VALUES = ['friday', 'saturday'];

interface RecReviewSearchProps {
	id: string;
	formRef: React.Ref<FormikProps<UserSearch>>;
	onSearch: () => void;
	onSaveAs: () => void;
	onDelete: () => void;
	onSave: () => void;
	onClose: () => void;
	onCreate: () => void;
	onReset: () => void;
	searchData: UserSearch;
	searchList: Array<UserSearch>;
	searchMetrics?: Array<SearchMetric>;
	setIsBlocked: (isBlocked: boolean) => void;
	loading?: boolean;
}

export const RecReviewSearch = ({
	id,
	formRef,
	onSearch,
	onSaveAs,
	onDelete,
	onSave,
	onReset,
	searchData,
	searchMetrics,
	searchList,
	onCreate,
	onClose,
	setIsBlocked,
	loading = false,
}: RecReviewSearchProps) => {
	const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
		useState<boolean>(false);
	const [saveAsBtnDisable, setSaveAsBtnDisable] = useState(true);
	const [searchBtnDisable, setSearchBtnDisable] = useState(true);
	/* This state is for maintaining confirmation type for modal */
	const [confMessageType, setConfMessageType] = useState('');
	const [showErrors, setShowErrors] = useState(false);
	const [disableSearchInput, setDisableSearchInput] = useState<boolean>(true);
	const [durationSelection, setDurationSelection] = useState<string>(
		searchData.endDate
			? CUSTOM_DATE_RANGE_VALUE
			: searchData.duration
				? String(searchData.duration)
				: '',
	);

	// Resyncs the dropdown's displayed selection when a different saved
	// search is loaded into this (non-remounted) component instance.
	useEffect(() => {
		setDurationSelection(
			searchData.endDate
				? CUSTOM_DATE_RANGE_VALUE
				: searchData.duration
					? String(searchData.duration)
					: '',
		);
	}, [searchData.searchId, searchData.duration, searchData.endDate]);

	const validateSearchName = (value: string) => {
		const checkName =
			searchList && searchList.length > 0
				? searchList.some((i) => i.searchName === value)
				: undefined;
		let error;
		if (!value) {
			error = labels.rec_review.search.form_error.required_search_name;
		} else if (checkName && searchData.searchType === 'new') {
			error = labels.rec_review.search.form_error.search_name_exist_err;
		} else if (checkName) {
			error = labels.rec_review.search.form_error.search_name_copy_err;
		} else if (value.length > 50) {
			error = labels.rec_review.search.form_error.max_search_name_err;
		} else {
			error = '';
		}
		return error;
	};

	const validateStatus = (value: string) => {
		let error;
		if (value.length <= 0) {
			error = labels.rec_review.search.form_error.required_status_err;
		} else {
			error = '';
		}
		return error;
	};

	const validateSelectedDays = (value: string) => {
		let error;
		if (value.length <= 0) {
			error = labels.rec_review.search.form_error.required_day_err;
		} else {
			error = '';
		}
		return error;
	};

	const { data: maxDateRangeData } = useGetMaxCustomDateRangeMonthsQuery();
	const maxCustomDateRangeMonths =
		maxDateRangeData?.getMaxCustomDateRangeMonths
			?.max_custom_date_range_months ?? 18;
	const maxAllowedEndDate = useMemo(
		() => dayjs().add(maxCustomDateRangeMonths, 'month'),
		[maxCustomDateRangeMonths],
	);

	const validateCustomRange = useCallback(
		(endDate?: string | null, startDate?: string | null) => {
			if (!endDate) {
				return labels.rec_review.search.form_error.required_day_err;
			}
			if (startDate && dayjs(endDate).isBefore(dayjs(startDate), 'day')) {
				return labels.rec_review.search.form_error
					.end_date_before_start_date_err;
			}
			if (dayjs(endDate).isAfter(maxAllowedEndDate, 'day')) {
				return labels.rec_review.search.form_error.custom_range_too_large_err.replace(
					'{months}',
					String(maxCustomDateRangeMonths),
				);
			}
			return '';
		},
		[maxAllowedEndDate, maxCustomDateRangeMonths],
	);

	const onConfirmationWindowClose = useCallback(
		() => setIsConfirmationModalOpen(false),
		[],
	);

	return (
		<>
			<Modal
				headerText={labels.rec_review.search.confirmation}
				isShown={isConfirmationModalOpen}
				hide={() => setIsConfirmationModalOpen(false)}
				onConfirm={() => {
					if (confMessageType === 'delete') {
						onDelete();
					} else {
						onClose();
					}
					setIsConfirmationModalOpen(false);
					setIsBlocked(false);
				}}
				onCancel={onConfirmationWindowClose}
				isConfirmationModal={true}
				confirmBtnText={labels.rec_review.search.yes}
				cancelBtnText={labels.rec_review.search.no}
				messageBody={
					confMessageType === 'delete'
						? labels.rec_review.search.delete_search_msg
						: labels.rec_review.search.discard_search_msg
				}
				hasIcon={true}
			/>
			<div className='search__container'>
				<div className='search__content'>
					<LoadingBar loading={loading} overlayStyle={{ borderRadius: '10px' }}>
						<Formik<UserSearch>
							enableReinitialize={true}
							initialValues={searchData}
							innerRef={formRef}
							onSubmit={() => {
								console.warn('');
							}}
						>
							{({ validateForm, values, errors, setFieldValue, dirty }) => {
								// Search is gated on validity only — not dirtiness (values matching
								// initialValues must still be searchable) and not isEditable
								// (that only protects Save/Delete of the stored search).
								setSearchBtnDisable(
									'statuses' in errors || 'selectedDays' in errors,
								);
								if (dirty) {
									setIsBlocked(true);
									if (searchData.searchType === 'new') {
										setSaveAsBtnDisable(true);
									} else {
										setSaveAsBtnDisable(false);
									}
									setDisableSearchInput(false);
								} else {
									setSaveAsBtnDisable(true);
									setDisableSearchInput(true);
									setShowErrors(false);
									setIsBlocked(false);
								}

								return (
									<Form id={id} className='search__content-form'>
										<div className='search__content-close'>
											<MdClose
												size={20}
												onClick={() => {
													if (dirty) {
														setConfMessageType('discard');
														setIsConfirmationModalOpen(true);
														setShowErrors(false);
													} else {
														onClose();
													}
												}}
											/>
										</div>
										<div className='search__content--left'>
											<div className='form-group'>
												<Field name='searchName' validate={validateSearchName}>
													{({ field }: any) => (
														<input
															type='text'
															placeholder='Search Name'
															disabled={
																!searchData.isEditable && disableSearchInput
															}
															onChange={() => {
																field.onChange(field.name);
															}}
															className='search__search-name'
															{...field}
														/>
													)}
												</Field>
											</div>

											<div className='search__buttons'>
												<div className='search__button-search-options'>
													<Button
														type='button'
														className={
															searchBtnDisable
																? 'search__button-search search__button--disable'
																: 'search__button-search search__button--enable'
														}
														onClick={() => {
															validateForm().then((errors) => {
																if (
																	Object.keys(errors).length === 0 ||
																	'searchName' in errors
																) {
																	onSearch();
																} else {
																	if ('searchName' in errors) {
																		onSearch();
																	}
																}
															});
														}}
													>
														{labels.rec_review.search.btn_search}
													</Button>
												</div>

												<div className='search__button-save-options'>
													<Button
														type='button'
														onClick={() => {
															validateForm().then((errors) => {
																console.warn('saveAs error', errors);
																if (Object.keys(errors).length === 0) {
																	onSaveAs();
																} else {
																	setShowErrors(true);
																}
															});
														}}
														className={
															saveAsBtnDisable &&
															(searchData.searchType === 'new'
																? true
																: !searchData.isEditable)
																? 'search__button-save-as search__button--disable'
																: 'search__button-save-as search__button--enable'
														}
													>
														{labels.rec_review.search.btn_save_as}
													</Button>
													<Button
														type='button'
														onClick={() => {
															validateForm().then((errors) => {
																if (
																	errors.searchName ===
																		labels.rec_review.search.form_error
																			.search_name_copy_err ||
																	Object.keys(errors).length === 0
																) {
																	onSave();
																} else {
																	setShowErrors(true);
																}
															});
														}}
														className={
															searchData.isEditable
																? 'search__button-save search__button--enable'
																: 'search__button-save search__button--disable'
														}
													>
														{labels.rec_review.search.btn_save}
													</Button>
												</div>
											</div>
											<div className='search__errors'>
												{errors.searchName && showErrors && (
													<p>{errors.searchName}</p>
												)}
												{errors.selectedDays && <p>{errors.selectedDays}</p>}
												{errors.statuses && <p>{errors.statuses}</p>}
											</div>
										</div>

										<div className='search__content--right'>
											{/* Hidden fields: keep Formik's field-level validation wired
											    up even though endDate/selectedDays/statuses now render
											    through plain shared components instead of Formik
											    <Field as={...}> controls. */}
											<Field
												name='endDate'
												validate={(value?: string | null) =>
													durationSelection === CUSTOM_DATE_RANGE_VALUE
														? validateCustomRange(value, values.startDate)
														: undefined
												}
											>
												{() => null}
											</Field>
											<Field
												name='selectedDays'
												validate={validateSelectedDays}
											>
												{() => null}
											</Field>
											<Field name='statuses' validate={validateStatus}>
												{() => null}
											</Field>

											<div
												className={
													durationSelection === CUSTOM_DATE_RANGE_VALUE
														? 'tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-3'
														: 'tw-grid tw-grid-cols-1 tw-gap-4 sm:tw-grid-cols-2'
												}
											>
												<div className='tw-flex tw-flex-col tw-gap-1'>
													<Label>
														{labels.rec_review.search.label_start_date}
													</Label>
													<DatePicker
														ariaLabel='Start date'
														date={
															values.startDate
																? dayjs(values.startDate).toDate()
																: undefined
														}
														disabled={(date) =>
															dayjs(date).isBefore(dayjs(), 'day')
														}
														onSelect={(date) =>
															setFieldValue(
																'startDate',
																date
																	? dayjs(date).format(dateFormatData)
																	: null,
															)
														}
													/>
												</div>
												{durationSelection === CUSTOM_DATE_RANGE_VALUE && (
													<div className='tw-flex tw-flex-col tw-gap-1'>
														<Label>
															{labels.rec_review.search.label_end_date}
														</Label>
														<DatePicker
															ariaLabel='End date'
															date={
																values.endDate
																	? dayjs(values.endDate).toDate()
																	: undefined
															}
															disabled={(date) =>
																dayjs(date).isBefore(
																	dayjs(values.startDate || new Date()),
																	'day',
																) ||
																dayjs(date).isAfter(maxAllowedEndDate, 'day')
															}
															onSelect={(date) =>
																setFieldValue(
																	'endDate',
																	date
																		? dayjs(date).format(dateFormatData)
																		: null,
																)
															}
														/>
														{errors.endDate && (
															<p className='tw-text-[0.8rem] tw-text-destructive'>
																{String(errors.endDate)}
															</p>
														)}
													</div>
												)}
												<div className='tw-flex tw-flex-col tw-gap-1'>
													<Label>
														{labels.rec_review.search.label_duration}
													</Label>
													<div className='tw-flex tw-items-center tw-gap-2'>
														<UiSelect
															value={durationSelection}
															onValueChange={(nextSelection: string) => {
																setDurationSelection(nextSelection);
																if (nextSelection === CUSTOM_DATE_RANGE_VALUE) {
																	setFieldValue('duration', undefined);
																} else {
																	setFieldValue(
																		'duration',
																		Number(nextSelection),
																	);
																	setFieldValue('endDate', undefined);
																}
															}}
														>
															<SelectTrigger className='tw-w-full'>
																<SelectValue />
															</SelectTrigger>
															<SelectContent>
																{days.map((option) => (
																	<SelectItem
																		key={option.value}
																		value={String(option.value)}
																	>
																		{option.label}
																	</SelectItem>
																))}
															</SelectContent>
														</UiSelect>
														{durationSelection !== CUSTOM_DATE_RANGE_VALUE && (
															<span className='tw-whitespace-nowrap'>
																{labels.rec_review.search.label_days}
															</span>
														)}
													</div>
												</div>
											</div>

											<div className='tw-grid tw-grid-cols-1 tw-gap-6 md:tw-grid-cols-2 tw-mt-4'>
												<div className='tw-flex tw-flex-col tw-gap-2'>
													<Label>
														{labels.rec_review.search.label_days_of_week}
													</Label>
													<div className='tw-flex tw-flex-wrap tw-gap-2'>
														<Button
															type='button'
															variant='outline'
															size='sm'
															onClick={() =>
																setFieldValue('selectedDays', ALL_DAY_VALUES)
															}
														>
															{labels.rec_review.search.btn_select_all_days}
														</Button>
														<Button
															type='button'
															variant='outline'
															size='sm'
															onClick={() => setFieldValue('selectedDays', [])}
														>
															{labels.rec_review.search.btn_deselect_all_days}
														</Button>
														<Button
															type='button'
															variant='outline'
															size='sm'
															onClick={() =>
																setFieldValue('selectedDays', WEEKDAY_VALUES)
															}
														>
															{labels.rec_review.search.btn_weekdays_only}
														</Button>
														<Button
															type='button'
															variant='outline'
															size='sm'
															onClick={() =>
																setFieldValue('selectedDays', WEEKEND_VALUES)
															}
														>
															{labels.rec_review.search.btn_weekends_only}
														</Button>
													</div>
													<div className='tw-flex tw-gap-1'>
														{weekDays.map((day) => {
															const isSelected = (
																values.selectedDays ?? []
															).includes(day.value);
															return (
																<Button
																	key={day.value}
																	type='button'
																	title={day.value}
																	variant={isSelected ? 'default' : 'outline'}
																	size='sm'
																	className='tw-w-8 tw-h-8 tw-p-0 tw-text-xs'
																	onClick={() => {
																		const current = values.selectedDays ?? [];
																		setFieldValue(
																			'selectedDays',
																			isSelected
																				? current.filter(
																						(value) => value !== day.value,
																					)
																				: [...current, day.value],
																		);
																	}}
																>
																	{day.label}
																</Button>
															);
														})}
													</div>
													<p className='tw-text-[0.8rem] tw-text-muted-foreground'>
														{
															labels.rec_review.search
																.label_days_of_week_description
														}
													</p>
												</div>

												<div className='tw-flex tw-flex-col tw-gap-2'>
													<Label>{labels.rec_review.search.label_status}</Label>
													<div className='tw-flex tw-flex-wrap tw-gap-2'>
														{statusOptions.map((option) => {
															const isSelected = (
																values.statuses ?? []
															).includes(option.value);
															return (
																<Button
																	key={option.value}
																	type='button'
																	variant={isSelected ? 'default' : 'outline'}
																	size='sm'
																	onClick={() => {
																		const current = values.statuses ?? [];
																		setFieldValue(
																			'statuses',
																			isSelected
																				? current.filter(
																						(value) => value !== option.value,
																					)
																				: [...current, option.value],
																		);
																	}}
																>
																	{option.label}
																</Button>
															);
														})}
													</div>
												</div>
											</div>
											<FieldArray name='searchCriteria'>
												{({ remove, push }) => (
													<div className='search__additional-search'>
														{values.searchCriteria?.map((value, index) => {
															return (
																<div
																	key={index}
																	className='search__additional-search-row'
																>
																	<div className='search__additional-search-row-left'>
																		<Field
																			name={`searchCriteria[${index}].lhsCode`}
																			key={`lhs_${index}`}
																		>
																			{({ field }: any) => (
																				<Select
																					displayEmpty
																					renderValue={
																						value?.lhsCode !== ''
																							? undefined
																							: () => {
																									return (
																										<div className='search__dropdown-placeholder'>
																											Select
																										</div>
																									);
																								}
																					}
																					inputProps={{}}
																					{...field}
																					className='search__additional-search-select'
																				>
																					{searchMetrics?.map((option, idx) => (
																						<MenuItem
																							key={`lhs_option_${idx}`}
																							value={option.lhsCode}
																						>
																							{option.lhsName}
																						</MenuItem>
																					))}
																				</Select>
																			)}
																		</Field>
																		<Field
																			name={`searchCriteria[${index}].operatorCode`}
																			key={`operator_${index}`}
																		>
																			{({ field }: any) => (
																				<Select
																					displayEmpty
																					renderValue={
																						value?.operatorCode !== ''
																							? undefined
																							: () => {
																									return (
																										<div className='search__dropdown-placeholder'>
																											Select
																										</div>
																									);
																								}
																					}
																					inputProps={{}}
																					{...field}
																					className='search__additional-search-select'
																				>
																					{searchMetrics
																						?.find(
																							(o: SearchMetric) =>
																								o.lhsCode === value?.lhsCode,
																						)
																						?.operator?.map((option, idx) => (
																							<MenuItem
																								key={`operator_option_${idx}`}
																								value={option?.code}
																							>
																								{option?.value}
																							</MenuItem>
																						))}
																				</Select>
																			)}
																		</Field>
																		<Field
																			name={`searchCriteria[${index}].rhsCode`}
																			key={`rhs_${index}`}
																		>
																			{({ field }: any) => (
																				<Select
																					displayEmpty
																					renderValue={
																						value?.rhsCode !== ''
																							? undefined
																							: () => {
																									return (
																										<div className='search__dropdown-placeholder'>
																											Select
																										</div>
																									);
																								}
																					}
																					inputProps={{}}
																					{...field}
																					className='search__additional-search-select'
																					onChange={(e) => {
																						setFieldValue(
																							`searchCriteria[${index}].rhsCode`,
																							e.target.value,
																						);
																						if (e.target.value === 'value') {
																						} else {
																							setFieldValue(
																								`searchCriteria[${index}].rhsCustomValue`,
																								null,
																							);
																						}
																					}}
																				>
																					{searchMetrics
																						?.find(
																							(o: SearchMetric) =>
																								o.lhsCode === value?.lhsCode,
																						)
																						?.rhs?.map((option, idx) => (
																							<MenuItem
																								key={`rhs_option_${idx}`}
																								value={
																									option?.code
																										? option?.code
																										: 'value'
																								}
																							>
																								{option?.value
																									? option?.value
																									: 'value'}
																							</MenuItem>
																						))}
																				</Select>
																			)}
																		</Field>
																	</div>

																	<div className='search__additional-search-row-right'>
																		{(value?.rhsCode === 'value' ||
																			value?.rhsCustomValue !== null) && (
																			<Field
																				name={`searchCriteria[${index}].rhsCustomValue`}
																			>
																				{() => (
																					<TextField
																						name={`searchCriteria[${index}].rhsCustomValue`}
																						className='search__additional-search-input'
																						placeholder={'Value'}
																						value={value?.rhsCustomValue ?? ''}
																						onChange={(e) => {
																							const regex = /^\d*\.?\d*$/;

																							if (regex.test(e.target.value)) {
																								setFieldValue(
																									`searchCriteria[${index}].rhsCustomValue`,
																									e.target.value,
																								);
																							}
																						}}
																					/>
																				)}
																			</Field>
																		)}
																		<div className='additional-search-action'>
																			<IconButton
																				disableFocusRipple
																				disableRipple
																				aria-label='remove-search-criteria'
																				onClick={() => remove(index)}
																			>
																				<MdOutlineIndeterminateCheckBox
																					size={22}
																					className='header__icon'
																					color={styles.colorWarning}
																				/>
																			</IconButton>
																		</div>
																	</div>
																</div>
															);
														})}
														<div className='search__footer'>
															<Button
																type='button'
																className='search__button-add-search search__button--enable'
																onClick={() =>
																	push({
																		lhsCode: '',
																		operatorCode: '',
																		rhsCode: '',
																		rhsCustomValue: null,
																	})
																}
															>
																{
																	labels.rec_review.search
																		.btn_add_search_criteria
																}
															</Button>
															<div className='search__other-actions'>
																<CustomTooltip
																	content={
																		<p>
																			{
																				labels.rec_review.search.tooltip
																					.create_search
																			}
																		</p>
																	}
																	placement='bottom'
																>
																	<IconButton
																		disableFocusRipple
																		disableRipple
																		aria-label='create-search'
																		className='search__create'
																		onClick={() => {
																			onCreate();
																		}}
																	>
																		<MdOutlineAdd size={22} />
																	</IconButton>
																</CustomTooltip>
																<CustomTooltip
																	content={
																		<p>
																			{labels.rec_review.search.tooltip.reset}
																		</p>
																	}
																	placement='bottom'
																>
																	<IconButton
																		disableFocusRipple
																		disableRipple
																		aria-label='reset'
																		className='search__reset search__reset--enable'
																		onClick={() => {
																			onReset();
																		}}
																	>
																		<MdRefresh size={22} />
																	</IconButton>
																</CustomTooltip>
																<CustomTooltip
																	content={
																		<p>
																			{
																				labels.rec_review.search.tooltip
																					.delete_search
																			}
																		</p>
																	}
																	placement='bottom'
																>
																	<IconButton
																		disableFocusRipple
																		disableRipple
																		aria-label='search-delete'
																		className={
																			searchData.isEditable
																				? 'search__delete search__delete--enable'
																				: 'search__delete  search__delete--disable'
																		}
																		onClick={() => {
																			setConfMessageType('delete');
																			setIsConfirmationModalOpen(true);
																		}}
																	>
																		<MdDeleteOutline size={22} />
																	</IconButton>
																</CustomTooltip>
															</div>
														</div>
													</div>
												)}
											</FieldArray>
										</div>
									</Form>
								);
							}}
						</Formik>
					</LoadingBar>
				</div>
			</div>
		</>
	);
};
